import {useCallback, useMemo, useState, type ReactNode} from 'react';
import {type Item} from '@/api/items';
import {type ImageMatchTier} from '@/api/jobs';
import {type FlagColor} from '@/api/flags';
import {ItemCard, type ItemExtraAction, type ItemTermMatches} from '@/components/items/ItemCard';
import {ItemListView} from '@/components/items/ItemListView';
import {ThumbnailHistoryDialog} from '@/components/items/ThumbnailHistoryDialog';
import {getTermMatches, itemSearchText, parseSearchQuery} from '@/lib/searchQuery';
import {cn} from '@/lib/utils';
import {useUiStore} from '@/store/uiStore';

// No stemming toggle exists in the views that use matchedTerms (unlike Investigations' smart
// filter) - stemming on by default catches more literal variants ("doctors" ~ "doctor") without a
// control to expose.
const STEM_MATCHES = true;

// Fixed-minimum auto-fill: as many 280px-plus columns as the page is wide, 12px apart.
export const ITEM_GRID_CLASS = 'grid gap-3 grid-cols-[repeat(auto-fill,minmax(280px,1fr))]';

export type ItemsCollectionProps = {
  readonly items: readonly Item[];
  // Defaults to the analyst's persisted choice.
  readonly view?: 'grid' | 'list' | 'detailed';
  readonly selectedItemIds?: ReadonlySet<string>;
  readonly onToggleCheck?: (id: string) => void;
  readonly onToggleAll?: (select: boolean) => void;
  readonly onClassify?: (item: Item) => void;
  readonly onClassifyChannel?: (item: Item) => void;
  readonly onSetFlag?: (item: Item, color: FlagColor | null) => void;
  readonly onDismiss?: (item: Item) => void;
  // Click anywhere on a card/row that isn't one of its own controls.
  readonly onOpen?: (item: Item) => void;
  readonly matchTiers?: ReadonlyMap<string, ImageMatchTier>;
  // The search query to explain matches against: each card's thumbnail shows which of its terms are
  // literally present in that item's own text.
  readonly searchTerm?: string;
  // Overrides ITEM_GRID_CLASS (the workspace's thumbnail-size slider sets its own columns).
  readonly gridClassName?: string;
  // Grid cards only; see ItemCard.
  readonly extraActions?: readonly ItemExtraAction[];
  readonly renderFooter?: (item: Item) => ReactNode;
  // A card's (or row's) Show more opens the caller's details sidebar on that item, with the items
  // around it so the sidebar can step through them; without it, a card expands in place.
  readonly onShowDetails?: (item: Item, items: readonly Item[]) => void;
};

const NO_SELECTION: ReadonlySet<string> = new Set();
const NOOP = () => undefined;

// The one grid/list of items, shared by Items, Investigations and the job views.
export function ItemsCollection({
  items,
  view,
  selectedItemIds = NO_SELECTION,
  onToggleCheck,
  onToggleAll,
  onClassify,
  onClassifyChannel,
  onSetFlag,
  onDismiss,
  onOpen,
  matchTiers,
  searchTerm,
  gridClassName,
  extraActions,
  renderFooter,
  onShowDetails
}: ItemsCollectionProps) {
  const storedView = useUiStore((s) => s.itemsView);
  const effectiveView = view ?? storedView;
  const parsedQuery = useMemo(
    () => (searchTerm ? parseSearchQuery(searchTerm) : null),
    [searchTerm]
  );

  // Computed once per items/query change rather than inline in the render map below -
  // getTermMatches stems every field of every item's text, and doing that on every render (e.g.
  // a selection toggle) is wasted work over what can be kilobytes of description text per item.
  const matchesByItemId = useMemo(() => {
    if (!parsedQuery) {
      return null;
    }

    const map = new Map<string, ItemTermMatches>();
    for (const item of items) {
      map.set(item.id, getTermMatches(itemSearchText(item), parsedQuery, STEM_MATCHES));
    }

    return map;
  }, [items, parsedQuery]);

  // Hands the sidebar this collection's own items, in on-screen order - one channel's when the view
  // is grouped - so its back/forward steps through what the analyst is looking at.
  const showDetails = useCallback(
    (item: Item) => {
      onShowDetails?.(item, items);
    },
    [onShowDetails, items]
  );

  // A selection column with nothing to select into stays inert rather than half-wired.
  const toggleCheck = useCallback(
    (id: string) => {
      onToggleCheck?.(id);
    },
    [onToggleCheck]
  );

  // One history dialog for the whole grid, opened by item id - not one per card.
  const [historyItemId, setHistoryItemId] = useState<string | null>(null);
  const showHistory = useCallback((item: Item) => {
    setHistoryItemId(item.id);
  }, []);

  if (effectiveView === 'list' || effectiveView === 'detailed') {
    return (
      <ItemListView
        items={items}
        detailed={effectiveView === 'detailed'}
        selectedItemIds={selectedItemIds}
        onToggleCheck={onToggleCheck ? toggleCheck : NOOP}
        {...(onToggleAll ? {onToggleAll} : {})}
        {...(onClassify ? {onClassify} : {})}
        {...(onClassifyChannel ? {onClassifyChannel} : {})}
        {...(onSetFlag ? {onSetFlag} : {})}
        {...(onDismiss ? {onDismiss} : {})}
        {...(onOpen ? {onOpen} : {})}
        {...(onShowDetails ? {onShowDetails: showDetails} : {})}
        {...(matchTiers ? {matchTiers} : {})}
      />
    );
  }

  return (
    <div className={cn(gridClassName ?? ITEM_GRID_CLASS)}>
      {historyItemId === null ? null : (
        <ThumbnailHistoryDialog
          open
          itemId={historyItemId}
          onClose={() => {
            setHistoryItemId(null);
          }}
        />
      )}
      {items.map((item) => (
        <ItemCard
          key={item.id}
          item={item}
          checked={selectedItemIds.has(item.id)}
          anyChecked={selectedItemIds.size > 0}
          {...(onToggleCheck ? {onToggleCheck: toggleCheck} : {})}
          {...(onClassify ? {onClassify} : {})}
          {...(onClassifyChannel ? {onClassifyChannel} : {})}
          {...(onSetFlag ? {onSetFlag} : {})}
          {...(onDismiss ? {onDismiss} : {})}
          {...(onOpen ? {onOpen} : {})}
          {...(matchTiers ? {matchTiers} : {})}
          {...(extraActions ? {extraActions} : {})}
          {...(renderFooter ? {renderFooter} : {})}
          {...(matchesByItemId ? {matchedTerms: matchesByItemId.get(item.id)!} : {})}
          {...(onShowDetails ? {onShowDetails: showDetails} : {})}
          onShowHistory={showHistory}
        />
      ))}
    </div>
  );
}
